/**
 * RaceHistory Component
 *
 * Displays a list of past races hosted by the user.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRaceHistory, type RaceHistoryEntry } from '@/hooks/useRaceHistory';
import { Pagination } from '@/components/Pagination';
import styles from './Race.module.css';

const ITEMS_PER_PAGE = 10;

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStateLabel(state: string): { label: string; className: string } {
  switch (state) {
    case 'finished':
      return { label: 'Finished', className: styles.stateFinished };
    case 'racing':
      return { label: 'In Progress', className: styles.stateRacing };
    case 'waiting':
      return { label: 'Waiting', className: styles.stateWaiting };
    default:
      return { label: state, className: '' };
  }
}

interface RaceHistoryCardProps {
  race: RaceHistoryEntry;
  onClick: () => void;
}

function RaceHistoryCard({ race, onClick }: RaceHistoryCardProps) {
  const stateInfo = getStateLabel(race.state);

  return (
    <button className={styles.historyCard} onClick={onClick}>
      <div className={styles.historyCardHeader}>
        <span className={styles.historyDate}>{formatDate(race.created_at)}</span>
        <span className={`${styles.historyState} ${stateInfo.className}`}>
          {stateInfo.label}
        </span>
      </div>
      <div className={styles.historyCardBody}>
        <div className={styles.historyStat}>
          <span className={styles.historyStatValue}>{race.participant_count}</span>
          <span className={styles.historyStatLabel}>Participants</span>
        </div>
        <div className={styles.historyStat}>
          <span className={styles.historyStatValue}>{race.question_count}</span>
          <span className={styles.historyStatLabel}>Questions</span>
        </div>
        <div className={styles.historyStat}>
          <span className={styles.historyStatValue}>{race.join_code}</span>
          <span className={styles.historyStatLabel}>Code</span>
        </div>
      </div>
    </button>
  );
}

export function RaceHistory() {
  const navigate = useNavigate();
  const { races, loading, error } = useRaceHistory();
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pagination
  const totalPages = Math.ceil(races.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRaces = races.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (loading) {
    return (
      <div className={styles.historyLoading}>
        <div className={styles.spinner} />
        <span>Loading race history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.historyError}>
        <span>Failed to load race history: {error}</span>
      </div>
    );
  }

  if (races.length === 0) {
    return (
      <div className={styles.historyEmpty}>
        <span>No races yet. Host your first race!</span>
      </div>
    );
  }

  const handleRaceClick = (race: RaceHistoryEntry) => {
    if (race.state === 'finished') {
      // Finished races go to stats page
      navigate(`/race/${race.id}`);
    } else {
      // Active races (waiting/racing) go to host page to resume
      navigate(`/race/host?resume=${race.id}`);
    }
  };

  return (
    <div className={styles.historyList}>
      {paginatedRaces.map((race) => (
        <RaceHistoryCard
          key={race.id}
          race={race}
          onClick={() => handleRaceClick(race)}
        />
      ))}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
